import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { DocumentEdit, DocumentEditAndReadDocument } from '@secca/shared/models/atlas/atlas-document-edit-and-read.model';

export interface DocumentEditTextDialogData {
  title: string;
  originalDocument: DocumentEditAndReadDocument;
  documentEdit: DocumentEdit;
}

interface TextComparisonRow {
  originalText: string;
  translatedText: string;
}

@Component({
  selector: 'app-document-edit-text-dialog',
  templateUrl: './document-edit-text-dialog.component.html',
  styleUrls: ['./document-edit-text-dialog.component.scss'],
  standalone: false
})
export class DocumentEditTextDialogComponent {
  readonly comparisonRows: TextComparisonRow[];

  constructor(
    private dialogRef: MatDialogRef<DocumentEditTextDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentEditTextDialogData,
    public dialogBoundryService: DialogBoundryService
  ) {
    this.comparisonRows = this.buildComparisonRows(data.documentEdit);
  }

  close(): void {
    this.dialogRef.close();
  }

  private buildComparisonRows(documentEdit: DocumentEdit): TextComparisonRow[] {
    const editRows = this.extractComparisonRows(documentEdit.text_edits);
    if (editRows.length > 0) {
      return editRows;
    }

    const originalSegments = this.getOriginalSegments();
    const translatedSegments = this.extractSegments(documentEdit.text_property_list);

    if (originalSegments.length > 0 || translatedSegments.length > 0) {
      const maxLength = Math.max(originalSegments.length, translatedSegments.length);
      return Array.from({ length: maxLength }, (_value, index) => ({
        originalText: originalSegments[index] || '-',
        translatedText: translatedSegments[index] || '-'
      }));
    }

    return [
      {
        originalText: this.data.originalDocument.loaded_content || '-',
        translatedText: JSON.stringify(documentEdit, null, 2)
      }
    ];
  }

  private getOriginalSegments(): string[] {
    const propertySegments = this.extractSegments(this.data.originalDocument.text_property_list);
    if (propertySegments.length > 0) {
      return propertySegments;
    }

    const loadedContent = this.data.originalDocument.loaded_content || '';
    return loadedContent
      .split(/\n+/)
      .map(segment => segment.trim())
      .filter(segment => !!segment);
  }

  private extractSegments(value: any): string[] {
    const results: string[] = [];
    this.collectSegments(value, results);
    return results.filter((segment, index) => segment && results.indexOf(segment) === index);
  }

  private extractComparisonRows(value: any): TextComparisonRow[] {
    const rows: TextComparisonRow[] = [];
    this.collectComparisonRows(value, rows);
    return rows.filter(row => row.originalText || row.translatedText);
  }

  private collectComparisonRows(value: any, rows: TextComparisonRow[]): void {
    if (value == null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => this.collectComparisonRows(item, rows));
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    const originalText = this.findFirstString(value, [
      'original',
      'original_text',
      'source_text',
      'input_text',
      'from_text',
      'before_text',
      'text_before',
      'old_text',
      'text'
    ]);
    const translatedText = this.findFirstString(value, [
      'new',
      'translated_text',
      'edited_text',
      'replacement_text',
      'replacement',
      'to_text',
      'after_text',
      'text_after',
      'new_text',
      'value',
      'content'
    ]);

    if (originalText || translatedText) {
      rows.push({
        originalText: originalText || '-',
        translatedText: translatedText || '-'
      });
      return;
    }

    Object.values(value).forEach(item => this.collectComparisonRows(item, rows));
  }

  private collectSegments(value: any, results: string[]): void {
    if (value == null) {
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        results.push(trimmed);
      }
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(item => this.collectSegments(item, results));
      return;
    }

    if (typeof value === 'object') {
      const prioritizedKeys = [
        'original_text',
        'translated_text',
        'edited_text',
        'replacement_text',
        'source_text',
        'input_text',
        'replacement',
        'text',
        'value',
        'content'
      ];

      for (const key of prioritizedKeys) {
        if (typeof value[key] === 'string' && value[key].trim()) {
          results.push(value[key].trim());
          return;
        }
      }

      Object.values(value).forEach(item => this.collectSegments(item, results));
    }
  }

  private findFirstString(value: any, keys: string[]): string | null {
    for (const key of keys) {
      if (typeof value[key] === 'string' && value[key].trim()) {
        return value[key].trim();
      }
    }

    return null;
  }
}
